/* ============================================================
   NavDrawer — UI-1 (edbdcb80): mobile navigation hub.
   Artem 2026-08-26: tabs keep growing and the wrapped inline
   navbar broke the layout. On <768px the inline row is hidden and
   a hamburger in the navbar opens this drawer: the full tab list
   (data-driven, any N) plus a session section (Войти anonymous /
   Профиль + Выйти authenticated — the desktop session block is
   hidden on mobile, so the drawer is its only mobile home).

   Rendered through a portal to document.body (UI-2/584b9b1b
   lesson): the sticky navbar has backdrop-filter, which makes it
   the containing block + stacking context of fixed descendants —
   an inline fixed drawer would collapse onto the navbar strip.
   Right-docked panel, overlay click / Escape close — same
   contract as NotificationDrawer and ProfileDrawer.
   ============================================================ */

import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { navItems, type NavSession } from './navigation';
import type { Page } from '../routing';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Current top-level page (active tab highlight). */
  page: Page;
  /** Session subset driving role-gated tabs + the session section. */
  session: NavSession;
  onNavigate: (page: Page) => void;
  /** Opens the profile drawer (authenticated sessions only). */
  onOpenProfile?: () => void;
  onLogout?: () => void;
}

export function NavDrawer({ open, onClose, page, session, onNavigate, onOpenProfile, onLogout }: Props) {
  // Close on Escape.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const items = navItems(session);

  const go = (p: Page) => {
    onNavigate(p);
    onClose();
  };

  const rowStyle = (active: boolean): React.CSSProperties => ({
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-md)',
    width: '100%',
    minHeight: 48,
    padding: '12px 14px',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--color-separator)',
    background: active ? 'rgba(255, 149, 0, 0.08)' : 'var(--color-surface)',
    cursor: 'pointer',
    textAlign: 'left',
  });

  const labelStyle = (active: boolean): React.CSSProperties => ({
    fontSize: 'var(--fs-h3)',
    fontWeight: active ? 'var(--fw-semibold)' : 'var(--fw-medium)',
    color: active ? 'var(--color-primary)' : 'var(--color-text-primary)',
  });

  return createPortal(
    <div
      role="dialog"
      aria-label="Меню"
      style={{ position: 'fixed', inset: 0, zIndex: 200, display: 'flex', justifyContent: 'flex-end' }}
    >
      {/* Overlay */}
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(0, 0, 0, 0.28)' }} />

      {/* Panel */}
      <aside
        style={{
          position: 'relative',
          width: 'min(340px, 100vw)',
          height: '100%',
          background: 'var(--color-surface)',
          borderLeft: '1px solid var(--color-separator)',
          padding: 'var(--space-lg)',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-sm)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-xs)' }}>
          <h2 style={{ fontSize: 'var(--fs-h3)', fontWeight: 'var(--fw-bold)', color: 'var(--color-text-primary)' }}>Меню</h2>
          <button
            onClick={onClose}
            aria-label="Закрыть"
            style={{
              fontSize: 'var(--fs-body)',
              color: 'var(--color-text-secondary)',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px 8px',
            }}
          >
            ✕
          </button>
        </div>

        {/* Tabs — data-driven, any N */}
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)' }}>
          {items.map((item) => {
            const active = item.page === page;
            return (
              <li key={item.page}>
                <button onClick={() => go(item.page)} style={rowStyle(active)}>
                  <span aria-hidden style={{ fontSize: '18px', width: 24, textAlign: 'center' }}>{item.icon}</span>
                  <span style={labelStyle(active)}>{item.label}</span>
                </button>
              </li>
            );
          })}
        </ul>

        {/* Session section — the desktop session block is hidden on mobile,
            so this drawer is its only mobile home. */}
        <div
          style={{
            borderTop: '1px solid var(--color-separator)',
            marginTop: 'var(--space-sm)',
            paddingTop: 'var(--space-md)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-sm)',
          }}
        >
          {session.authenticated ? (
            <>
              <button
                onClick={() => {
                  onOpenProfile?.();
                  onClose();
                }}
                disabled={!onOpenProfile}
                style={rowStyle(false)}
              >
                <span aria-hidden style={{ fontSize: '18px', width: 24, textAlign: 'center' }}>👤</span>
                <span style={labelStyle(false)}>Профиль</span>
              </button>
              <button
                onClick={() => {
                  onLogout?.();
                  onClose();
                }}
                disabled={!onLogout}
                style={rowStyle(false)}
              >
                <span aria-hidden style={{ fontSize: '18px', width: 24, textAlign: 'center' }}>🚪</span>
                <span style={labelStyle(false)}>Выйти</span>
              </button>
            </>
          ) : (
            <button onClick={() => go('auth')} style={rowStyle(false)}>
              <span aria-hidden style={{ fontSize: '18px', width: 24, textAlign: 'center' }}>🔓</span>
              <span style={labelStyle(false)}>Войти</span>
            </button>
          )}
        </div>
      </aside>
    </div>,
    document.body,
  );
}
